import { Injectable, inject } from '@angular/core';
import { catchError, firstValueFrom, tap, map as rxMap, throwError } from 'rxjs';
import { AuthenticationService } from '../../authentication/authentication.service';
import { FirebaseEventService } from '../../authentication/firebase.event.service';
import { ApiException, MceApiService, ProjectCreateDto, ProjectDto, ProjectUpdateRequestDto } from '../../core/mceapi.client.generated';
import { appOpt, getApiErrorMessage, optionToApiOptionDto } from '../../core/mceapi.client.helpers';
import { DialogService } from '../../shared/dialogs/dialog.service';
import { bind, fromNullable } from '../../shared/functions/functional';

@Injectable({
  providedIn: 'root',
})
export class ProjectEditService {

  private authenticationService = inject(AuthenticationService);
  private readonly mceApiService = inject(MceApiService);
  private firebaseEventService = inject(FirebaseEventService);
  private dialogService = inject(DialogService);

  /// Creates a new project and returns the created project.
  /// Not saved in the database until the project is saved.
  async createProject(): Promise<ProjectCreateDto> {
    return firstValueFrom(this.mceApiService.v1ProjectsCreateProject());
  }

  async getProjectComments(projectUid: string) {
    return firstValueFrom(
      this.mceApiService.v1ProjectsComments(projectUid).pipe(
        rxMap(x => appOpt(x)))
    );
  }

  async copyProject(project: ProjectDto): Promise<ProjectDto> {
    const projectUid = project.uid;
    if (!projectUid) throw new Error('Cannot copy a project without a UID.');

    return firstValueFrom(
      this.mceApiService.v1ProjectsCopy(projectUid).pipe(
        tap(copiedProject => {
          this.firebaseEventService.logCopyProjectEvent(
            this.authenticationService.currentUser(), project, copiedProject);
        }))

    );
  }

  async deleteProject(project: ProjectDto): Promise<void> {
    const projectUid = project.uid;
    if (!projectUid) throw new Error('Cannot delete a project without a UID.');

    return firstValueFrom(
      this.mceApiService.v1ProjectsDelete(projectUid).pipe(
        tap(() => {
          this.firebaseEventService.logDeleteProjectEvent(
            this.authenticationService.currentUser(), project);
        }))
    );
  }

  async saveNewProject(request: ProjectUpdateRequestDto): Promise<ProjectDto> {
    return firstValueFrom(
      this.mceApiService.v1ProjectsUpdate(request).pipe(
        tap(savedProject => {
          this.firebaseEventService.logAddProjectEvent(
            this.authenticationService.currentUser(), savedProject);
        }),
        catchError(error => this.handleUpdateProjectError(error)))
    );
  }

  async updateProject(request: ProjectUpdateRequestDto): Promise<ProjectDto> {
    return firstValueFrom(
      this.mceApiService.v1ProjectsUpdate(request).pipe(
        tap(updatedProject => {
          this.firebaseEventService.logSaveProjectEvent(
            this.authenticationService.currentUser(), updatedProject);
        }),
        catchError(error => this.handleUpdateProjectError(error)))
    );
  }

  private handleUpdateProjectError(error: unknown) {
    if (ApiException.isApiException(error) && error.status === 400) {
      this.dialogService.acknowledge(
        'Unable to save project',
        getApiErrorMessage(error),
        '320px'
      ).subscribe();
    }

    return throwError(() => error);
  }
  
}
